import { useQuery, useQueryClient } from '@tanstack/react-query';
import { notificationsApi } from '@/api/notifications-api';

/**
 * useNotifications — fetches recent notifications and polls unread count.
 *
 * Polling cadence: 60s for count (cheap), on-demand for list. The bell
 * badge is driven by count; the HamburgerSheet opening triggers a fresh
 * list fetch via TanStack Query invalidation.
 */

const COUNT_POLL_MS = 60_000;

export function useNotifications(opts: { recentLimit?: number } = {}) {
  const qc = useQueryClient();
  const recentLimit = opts.recentLimit ?? 8;

  const countQuery = useQuery({
    queryKey: ['notifications', 'count'],
    queryFn: notificationsApi.unreadCount,
    refetchInterval: COUNT_POLL_MS,
    refetchOnWindowFocus: true,
  });

  const recentQuery = useQuery({
    queryKey: ['notifications', 'recent', recentLimit],
    queryFn: () => notificationsApi.list({ page: 1, pageSize: recentLimit }),
  });

  return {
    unreadCount: countQuery.data?.count ?? 0,
    recent: recentQuery.data?.items ?? [],
    isLoading: recentQuery.isLoading,
    isError: recentQuery.isError,
    refetch: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
    async markRead(ids: string[]) {
      await notificationsApi.markRead(ids);
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
    async markAllRead() {
      await notificationsApi.markAllRead();
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  };
}
